<div class="billing-history-container">
  <div class="billing-history-header">
    <h3 class="section-title">Billing History</h3>
  </div>
  <!-- Totals Card and Date Controls Row -->
  @if (!error) {
    <div class="controls-row">
      <div class="header-right">
        <span
          class="date-range-text date-picker-trigger"
          [style.cursor]="'pointer'"
          (click)="startPicker.open(); $event.stopPropagation()"
        >
          {{ getDateRangeStartText() }}
        </span>
        <input
          matInput
          [matDatepicker]="startPicker"
          [(ngModel)]="customStartDate"
          (dateChange)="onStartDateChange($event)"
          style="
            position: absolute;
            opacity: 0;
            pointer-events: none;
            width: 0;
            height: 0;
            margin: 0;
            padding: 0;
          "
        />
        <mat-datepicker #startPicker style="display: none"></mat-datepicker>

        <span>—</span>

        <span
          class="date-range-text date-picker-trigger"
          [style.cursor]="'pointer'"
          (click)="endPicker.open(); $event.stopPropagation()"
        >
          {{ getDateRangeEndText() }}
        </span>
        <input
          matInput
          [matDatepicker]="endPicker"
          [(ngModel)]="customEndDate"
          (dateChange)="onEndDateChange($event)"
          style="
            position: absolute;
            opacity: 0;
            pointer-events: none;
            width: 0;
            height: 0;
            margin: 0;
            padding: 0;
          "
        />
        <mat-datepicker #endPicker style="display: none"></mat-datepicker>
        <div class="custom-date-selector" [class.open]="isDropdownOpen">
          <div class="dropdown-trigger" (click)="toggleDropdown($event)">
            <span class="selected-value">{{ selectedDateRange.label }}</span>
            <mat-icon class="dropdown-arrow">arrow_drop_down</mat-icon>
          </div>
          @if (isDropdownOpen) {
            <div class="dropdown-menu" (click)="$event.stopPropagation()">
              @for (option of dateRangeOptions; track option.key) {
                <div
                  class="dropdown-option"
                  [class.selected]="option.key === selectedDateRange.key"
                  (click)="selectDateRange(option)"
                >
                  {{ option.label }}
                </div>
              }
            </div>
          }
        </div>
      </div>
    </div>
  }

  <!-- Per-type breakdown: proportional distribution bar above per-type tiles.
       Surfaces the by-type split that previously lived behind the "Total" modal. -->
  @if (!error && hasBillingHistory()) {
    <div class="billing-breakdown">
      <!-- Decorative: the same figures are exposed by the tiles below. -->
      <div class="distribution-bar" aria-hidden="true">
        @if (isLoading) {
          <div class="bar-track">
            <span class="skeleton-line bar-skeleton"></span>
          </div>
        } @else {
          <div class="bar-track" (mouseleave)="hoveredBarType = null">
            @for (segment of billingBarSegments; track segment.key) {
              <div
                class="bar-segment type-{{ segment.key }}"
                [style.width.%]="segment.widthPercent"
                (mouseenter)="hoveredBarType = segment.key"
              ></div>
            }
          </div>
        }
      </div>

      <div class="breakdown-tiles">
        @for (tile of billingBreakdown; track tile.key) {
          <div
            class="breakdown-tile type-{{ tile.key }}"
            [class.is-zero]="!isLoading && tile.isZero"
            [class.is-emphasized]="hoveredBarType === tile.key"
          >
            <div class="tile-header">
              <span class="tile-dot"></span>
              <span class="tile-label">{{ tile.label }}</span>
            </div>
            @if (isLoading) {
              <div class="skeleton-line short"></div>
            } @else {
              <div class="tile-value-row">
                <span class="tile-value">{{ tile.valueDisplay }}</span>
                @if (tile.percentDisplay) {
                  <span class="tile-percent">{{ tile.percentDisplay }}</span>
                }
              </div>
            }
          </div>
        }

        <!-- Grand total (shared tile). Outlined by default; the outline moves
             to a type tile while a distribution-bar segment is hovered. -->
        <app-billing-total-tile
          [value]="grandTotalDisplay"
          [loading]="isLoading"
          [emphasized]="!hoveredBarType"
        />
      </div>
    </div>
  }

  <!-- Error State -->
  @if (error && !isLoading) {
    <div class="error-state">
      <mat-icon class="error-icon">error</mat-icon>
      <p>{{ error }}</p>
      <button mat-raised-button color="primary" (click)="retry()">
        <mat-icon>refresh</mat-icon>
        Retry
      </button>
    </div>
  }

  <!-- Data Table Container (Always rendered to preserve paginator instance) -->
  <div
    class="table-container"
    [style.display]="error || !hasBillingHistory() ? 'none' : 'block'"
  >
    <table
      mat-table
      [dataSource]="dataSource"
      matSort
      multiTemplateDataRows
      class="billing-table"
    >
      <!-- Client Matter Column -->
      <ng-container matColumnDef="clientMatter">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Client Matter</th>
        <td mat-cell *matCellDef="let element">
          @if (isSkeletonRow(element)) {
            <app-matter-cell [matterNumber]="''" [loading]="true" />
          } @else {
            <app-matter-cell
              [matterNumber]="element.clientMatterNumber"
              [matterName]="element.matterName"
              [clientName]="element.clientName"
              [link]="!!element.clientMatterNumber"
              (clicked)="navigateToMatter($event)"
            />
          }
        </td>
      </ng-container>

      <!-- Billing Type Column -->
      <ng-container matColumnDef="billedType">
        <th mat-header-cell *matHeaderCellDef mat-sort-header start="desc">
          Billing Type
        </th>
        <td mat-cell *matCellDef="let element" class="numeric-cell">
          @if (isSkeletonRow(element)) {
            <div class="skeleton-line short"></div>
          } @else {
            {{ getBilledTypeLabel(element.billedType) }}
          }
        </td>
      </ng-container>

      <!-- Total Hours Column -->
      <ng-container matColumnDef="totalHours">
        <th mat-header-cell *matHeaderCellDef mat-sort-header start="desc">
          Total Hours
        </th>
        <td
          mat-cell
          *matCellDef="let element"
          class="numeric-cell total-hours-cell"
        >
          @if (isSkeletonRow(element)) {
            <div class="skeleton-line short"></div>
          } @else {
            {{ formatHours(element.totalHours) }}
          }
        </td>
      </ng-container>


      <!-- Task-breakdown expander. Header cell is intentionally empty; the toggle is
           unlabelled and announced via aria-label on each button. -->
      <ng-container matColumnDef="expand">
        <th mat-header-cell *matHeaderCellDef aria-label="Task breakdown"></th>
        <td mat-cell *matCellDef="let element" class="expand-cell">
          @if (canExpand(element)) {
            <button
              type="button"
              class="expand-toggle"
              [attr.aria-expanded]="expansion.isExpanded(element)"
              [attr.aria-controls]="expansion.panelId(element)"
              [attr.aria-label]="
                expansion.isExpanded(element)
                  ? 'Hide task breakdown'
                  : 'Show task breakdown'
              "
              (click)="expansion.toggle(element)"
            >
              @if (expansion.label(element)) {
                <span class="task-count">{{ expansion.label(element) }}</span>
              }
              <mat-icon
                class="expand-icon"
                [class.is-expanded]="expansion.isExpanded(element)"
                >chevron_right</mat-icon
              >
            </button>
          }
        </td>
      </ng-container>

      <!-- Detail row rendered beneath an expanded row. Spans every column. -->
      <ng-container matColumnDef="taskBreakdown">
        <td
          mat-cell
          *matCellDef="let element"
          class="task-breakdown-cell"
          [attr.colspan]="displayedColumns.length"
          [id]="expansion.panelId(element)"
          [attr.aria-hidden]="expansion.isExpanded(element) ? null : 'true'"
        >
          @if (expansion.isExpanded(element)) {
            <app-task-breakdown
              [tasks]="element.taskSummary"
              animate.enter="is-entering"
              animate.leave="is-leaving"
            />
          }
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr
        mat-row
        *matRowDef="let row; columns: displayedColumns"
        class="billing-row"
        [class.is-expanded]="expansion.isExpanded(row)"
      ></tr>
      <tr
        mat-row
        *matRowDef="let row; columns: ['taskBreakdown']"
        class="task-breakdown-row"
      ></tr>
    </table>
    <mat-paginator
      #paginator
      [length]="billingData.length"
      [pageSize]="pageSize"
      [pageSizeOptions]="[5, 10, 25, 50]"
      showFirstLastButtons
      aria-label="Select page of billing history"
      (page)="onPageChange($event)"
    >
    </mat-paginator>
  </div>

  <!-- Empty State -->
  @if (!isLoading && !error && !hasBillingHistory()) {
    <div class="empty-state">
      <mat-icon class="empty-icon">description</mat-icon>
      <p>
        No billing history has been entered. Billing activity will be displayed
        here after time entries have been logged.
      </p>
    </div>
  }
</div>
